import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AngularFirestore } from '@angular/fire/firestore';
import firebase from 'firebase/app';
import { from, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class WebhookService {

  private cloudFunctionUrl = environment.apiBaseUrl;

  constructor(private http: HttpClient, private firestore: AngularFirestore) {}

  processWebhook(webhookData: any) {
    return from(this.queueWebhookNotificationData(webhookData)).pipe(
      catchError((error) => {
        console.error('Error processing webhook:', error);
        return throwError(() => new Error('Webhook processing failed'));
      })
    );
  }

  // Enhanced webhook processing with logs collection update
  private async queueWebhookNotificationData(data: any): Promise<any> {
    console.log('Processing webhook data:', data);

    // Use checkout ID or reference as the unique identifier
    const eventId = data.checkout?.id || data.id || data.reference_number;
    
    if (!eventId) {
      console.error('No valid event ID found in webhook data');
      return null;
    }

    const existingEvent: any = await this.firestore.collection('webhookEvents').doc(eventId).get().toPromise();
    if (existingEvent.exists) {
      console.log(`Event with id ${eventId} has already been processed.`);
      return existingEvent.data();
    }

    // Store webhook event
    await this.firestore.collection('webhookEvents').doc(eventId).set({
      ...data,
      processedAt: firebase.firestore.FieldValue.serverTimestamp(),
      eventId: eventId
    });

    // Process based on the status
    const status = data.status || data.checkout?.status;
    switch (status) {
      case 'PAYMENT_SUCCESS':
      case 'COMPLETED':
      case 'SUCCESS':
        console.log(`Payment successful for checkout ID ${eventId}`);
        return await this.handleSuccessfulPayment(eventId, data);

      case 'PAYMENT_FAILED':
      case 'FAILED':
        console.log(`Payment failed for checkout ID ${eventId}`);
        return await this.updatePaymentCollections(eventId, 'failed', data);

      case 'PAYMENT_CANCELLED':
      case 'CANCELLED':
      case 'EXPIRED':
        console.log(`Payment cancelled/expired for checkout ID ${eventId}`);
        return await this.updatePaymentCollections(eventId, 'cancelled', data);

      default:
        console.log(`Unknown status: ${status} for checkout ID ${eventId}`);
        return null;
    }
  }

  private async handleSuccessfulPayment(checkoutId: string, webhookData: any): Promise<any> {
    try {
      // Update both online_payments and logs collections
      const result = await this.updatePaymentCollections(checkoutId, 'paid', webhookData);
      
      if (result && result.parkingSessionId) {
        // Update the parking logs collection
        await this.updateParkingLogsCollection(result.parkingSessionId, webhookData);
      }
      
      return result;
    } catch (error) {
      console.error('Error handling successful payment:', error);
      throw error;
    }
  }

  private async updatePaymentCollections(checkoutId: string, status: string, webhookData: any): Promise<any> {
    try {
      // Find the online payment record
      const onlinePaymentQuery = await this.firestore.collection('online_payments', ref => 
        ref.where('checkout_id', '==', checkoutId)
      ).get().toPromise();

      if (onlinePaymentQuery?.empty) {
        console.log(`No online payment found for checkoutId ${checkoutId}`);
        return null;
      }

      const onlinePaymentDoc = onlinePaymentQuery?.docs[0];
      const paymentData = onlinePaymentDoc?.data() as any;

      // Update online payment record
      await this.firestore.collection('online_payments').doc(onlinePaymentDoc?.id).update({
        status: status,
        payment_status: status,
        updated_at: firebase.firestore.FieldValue.serverTimestamp(),
        webhook_data: webhookData,
        payment_date: status === 'paid' ? firebase.firestore.FieldValue.serverTimestamp() : null
      });

      console.log(`Online Payment updated with checkoutId ${checkoutId}, status: ${status}`);

      return {
        success: true,
        paymentId: onlinePaymentDoc?.id,
        parkingSessionId: paymentData?.parking_session_id,
        status: status,
        paymentData: paymentData
      };

    } catch (error) {
      console.error(`Error updating payment collections for ${checkoutId}:`, error);
      throw error;
    }
  }

  private async updateParkingLogsCollection(sessionId: string, webhookData: any): Promise<any> {
    try {
      // Use cloud function to update logs collection with proper validation
      const response = await this.http.post(`${this.cloudFunctionUrl}/updateParkingSessionPayment`, {
        session_id: sessionId,
        webhook_data: webhookData,
        payment_status: 'paid',
        payment_date: new Date().toISOString()
      }).toPromise();

      console.log(`Parking logs updated for session ${sessionId}`);
      return response;

    } catch (error) {
      console.error(`Error updating parking logs for session ${sessionId}:`, error);
      
      // Fallback: Direct Firestore update if cloud function fails
      try {
        const logsQuery = await firebase.firestore().collection('logs')
          .where('id', '==', sessionId)
          .limit(1)
          .get();

        if (!logsQuery.empty) {
          const logDoc = logsQuery.docs[0];
          await logDoc.ref.update({
            paid: true,
            amount_paid: webhookData.totalAmount?.value || webhookData.amount,
            date_paid: firebase.firestore.FieldValue.serverTimestamp(),
            payment_method: 'maya',
            webhook_processed: true,
            updated_at: firebase.firestore.FieldValue.serverTimestamp()
          });

          console.log(`Parking logs updated directly for session ${sessionId}`);
        }
      } catch (fallbackError) {
        console.error(`Fallback update failed for session ${sessionId}:`, fallbackError);
        throw fallbackError;
      }
    }
  }

  // Method to manually verify and sync payment status
  async verifyAndSyncPayment(checkoutId: string): Promise<any> {
    try {
      const response = await this.http.post(`${this.cloudFunctionUrl}/verifyPaymentStatus`, {
        checkout_id: checkoutId
      }).toPromise();

      return response;
    } catch (error) {
      console.error('Error verifying payment status:', error);
      throw error;
    }
  }
}
